"use client";

import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { useMarketingText } from "@/lib/i18n/marketingClient";

/**
 * Marketing-only illustration of the Continuity Guardian's in-editor experience:
 * a manuscript line flagged with the same red wavy underline the editor draws
 * (see ContinuityHighlightExtension), and the same card the editor pops over a
 * flag (see ContinuityIssuePopover) — quote, the earlier line it conflicts with,
 * a suggested fix, and the jump action. Not live AI; the copy is a fixed example.
 * Click (or hover) the flagged line to toggle its card, mirroring the real feature.
 */
export function ContinuityGuardianGlimpse() {
  const [open, setOpen] = useState(true);
  const localize = useMarketingText();

  return (
    <figure
      aria-label={localize(
        "Novilot flagging a contradicting line in a manuscript, with a card showing the conflicting text from an earlier chapter and a suggested fix.",
      )}
      className="w-full max-w-xl mx-auto lg:max-w-none"
    >
      <div className="rounded-2xl border border-border/80 bg-muted/20 p-5 shadow-sm">
        {/* Manuscript excerpt with one flagged line */}
        <div className="rounded-lg border border-border/80 bg-background px-4 py-3.5 text-[15px] leading-[1.75] text-foreground font-serif">
          <span className="mb-2 block font-sans text-xs uppercase tracking-wider text-muted-foreground">
            {localize("Chapter 9")}
          </span>
          {localize("Elara turned from the window, and for a moment")}{" "}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            onMouseEnter={() => setOpen(true)}
            aria-expanded={open}
            aria-controls="continuity-example-card"
            className="-my-2 inline-flex min-h-11 cursor-pointer items-center rounded bg-transparent px-1 py-2 font-[inherit] underline decoration-wavy underline-offset-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500/60"
            style={{
              textDecorationColor: "#dc2626",
              backgroundColor: "rgba(220,38,38,0.10)",
            }}
          >
            {localize("her eyes flashed green")}
          </button>{" "}
          {localize("in the torchlight before she looked away.")}
        </div>

        {/* The card the editor shows over a flag — same fields as the real popover */}
        {open && (
          <div
            id="continuity-example-card"
            role="region"
            aria-label={localize("Continuity error card")}
            className="mx-auto mt-3 w-full max-w-sm rounded-xl border border-border bg-background p-3 shadow-lg"
          >
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <AlertCircle className="h-3.5 w-3.5 text-red-500" aria-hidden /> {localize("Continuity error")}
            </div>
            <p className="text-sm font-semibold">{localize("Elara’s eye color changed")}</p>
            <p className="mt-1 border-s-2 border-amber-400/70 ps-2 text-sm italic text-foreground/80">
              {localize("“her eyes flashed green”")}
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {localize("Conflicts with “her cool blue eyes” in Chapter 1")}
            </p>
            <p className="mt-1.5 text-sm text-accent">
              {localize("Fix: Change to blue to match Chapter 1, or update the story bible.")}
            </p>
          </div>
        )}
      </div>
    </figure>
  );
}
